import React, { useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { LayoutDashboard } from 'lucide-react'
import { queryInstant } from '@/core/observability_api_client'
import type { ServiceStatus, PlatformStats } from '@/core/observability_types'
import type { PromQLResult } from '@/core/observability_types'
import { StatsRow } from './StatsRow'
import { ServiceHealthGrid } from './ServiceHealthGrid'

function parseResultMap(results: PromQLResult[]): Map<string, number> {
  const map = new Map<string, number>()
  for (const r of results) {
    const name = r.metric['service_name'] || r.metric['job'] || r.metric['__name__'] || ''
    if (name && r.value) {
      map.set(name, parseFloat(r.value[1]) || 0)
    }
  }
  return map
}

export const OverviewPage: React.FC = () => {
  const { t } = useTranslation('pages')
  const navigate = useNavigate()
  const [services, setServices] = useState<ServiceStatus[]>([])
  const [stats, setStats] = useState<PlatformStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchMetrics = useCallback(async () => {
    try {
      const [upRes, rateRes, errorRes, p95Res] = await Promise.all([
        queryInstant('max(up{job=~"analytics.*|auth.*|governance.*|orchestration.*|streaming.*|infra.*"}) by (job)'),
        queryInstant('sum(rate(http_server_request_duration_count[5m])) by (job)'),
        queryInstant('sum(rate(http_server_request_duration_count{http_status_code=~"5.."}[5m])) by (job) / sum(rate(http_server_request_duration_count[5m])) by (job)'),
        queryInstant('histogram_quantile(0.95, sum(rate(http_server_request_duration_bucket[5m])) by (le, job))'),
      ])

      // Build service list from up{} targets (Prometheus scrape discovery)
      const upResults = upRes.data?.result || []
      const rateMap = parseResultMap(rateRes.data?.result || [])
      const errorMap = parseResultMap(errorRes.data?.result || [])
      const p95Map = parseResultMap(p95Res.data?.result || [])

      const serviceList: ServiceStatus[] = upResults.map(r => {
        const name = r.metric['job'] || 'unknown'
        const isUp = r.value ? parseFloat(r.value[1]) === 1 : false
        const rate = rateMap.get(name) || 0
        const errRate = errorMap.get(name) || 0
        const p95 = (p95Map.get(name) || 0) * 1000

        return {
          name,
          status: isUp ? 'up' as const : 'down' as const,
          requestRate: rate,
          errorRate: isNaN(errRate) ? 0 : errRate,
          latencyP50: 0,
          latencyP95: isNaN(p95) ? 0 : p95,
          latencyP99: 0,
        }
      })

      // Deduplicate by service name (take first match per service)
      const seen = new Set<string>()
      const dedupedServices = serviceList.filter(s => {
        if (seen.has(s.name)) return false
        seen.add(s.name)
        return true
      })

      setServices(dedupedServices)

      // Compute platform stats
      const totalRate = dedupedServices.reduce((sum, s) => sum + s.requestRate, 0)
      const totalErrors = dedupedServices.reduce((sum, s) => sum + (s.requestRate * s.errorRate), 0)
      const maxP95 = Math.max(...dedupedServices.map(s => s.latencyP95), 0)

      setStats({
        totalServices: dedupedServices.length,
        healthyServices: dedupedServices.filter(s => s.status === 'up').length,
        totalRequestRate: totalRate,
        globalErrorRate: totalRate > 0 ? totalErrors / totalRate : 0,
        globalP95Latency: maxP95,
      })

      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch metrics')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchMetrics()
    const interval = setInterval(fetchMetrics, 30000) // refresh every 30s
    return () => clearInterval(interval)
  }, [fetchMetrics])

  return (
    <div className="container-standard">
      <div className="page-header">
        <div className="flex items-center gap-3">
          <LayoutDashboard className="h-6 w-6 text-slate-600 dark:text-slate-400" />
          <div>
            <h1 className="page-title">{t('observability.overviewTitle', 'Platform Overview')}</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {t('observability.overviewDescription', 'Real-time health and performance of all platform services')}
            </p>
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4 mb-6">
          <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
        </div>
      )}

      <div className="space-y-6">
        <StatsRow stats={stats} loading={loading} />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        </div>
        <div>
          <h2 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-4">
            {t('observability.serviceHealth', 'Service Health')}
          </h2>
          <ServiceHealthGrid
            services={services}
            loading={loading}
            onServiceClick={(name) => navigate(`/infrastructure/services?service=${encodeURIComponent(name)}`)}
          />
        </div>
      </div>
    </div>
  )
}
